<template>
  <div class="animated fadeIn">
    <b-card>
      <template v-slot:header>
        <h5 class="mb-0">Pemulihan Pembayaran</h5>
      </template>

      <b-form @submit.prevent="checkPayment">
        <div class="form-content-wrapper">
          <b-form-group label="No. Kwitansi Pembayaran" label-for="invoice-number">
            <b-form-input
              id="invoice-number"
              v-model.trim="input.invoiceNumber"
              name="invoiceNumber"
              placeholder="Masukkan no. kwitansi pembayaran"
              required
            />
          </b-form-group>

          <b-form-group label="Passcode" label-for="passcode">
            <b-form-input
              id="passcode"
              v-model.trim="input.passcode"
              type="password"
              name="passcode"
              placeholder="Masukkan passcode"
              required
            />
          </b-form-group>

          <b-form-checkbox
            v-model="input.transaksiKeBulanBerikutnya"
            name="transaksiKeBulanBerikutnya"
          >
            Transaksi ke bulan berikutnya
          </b-form-checkbox>
        </div>

        <div class="form-wrapper-btn submit-btn-wrapper">
          <b-button
            type="submit"
            variant="pgiBtn"
            :disabled="isChecking || isSubmitting"
          >
            <b-spinner v-if="isChecking" small type="grow" />
            {{ isChecking ? 'Memeriksa...' : 'Cek Pembayaran' }}
          </b-button>
        </div>
      </b-form>
    </b-card>

    <b-card v-if="hasChecked" class="mt-3">
      <template v-slot:header>
        <h5 class="mb-0">Hasil Pengecekan</h5>
      </template>

      <b-row>
        <b-col md="6">
          <h5 class="section-title">Sistem PGI</h5>
          <div class="section-content">
            <p>
              Status DOKU
              <b-badge class="float-right" :variant="dokuLocalStatusVariant">
                {{ dokuLocalStatus }}
              </b-badge>
            </p>
            <p class="mb-0">
              Status Pembayaran
              <b-badge class="float-right" :variant="paymentLocalStatusVariant">
                {{ paymentLocalStatus }}
              </b-badge>
            </p>
          </div>
        </b-col>
        <b-col md="6">
          <h5 class="section-title">DOKU</h5>
          <div class="section-content">
            <p class="mb-0">
              Status DOKU
              <b-badge class="float-right" :variant="invoice.status === 10 ? 'success' : 'warning'">
                {{ invoice.status_description || 'Tidak diketahui' }}
              </b-badge>
            </p>
          </div>
        </b-col>
      </b-row>
      <hr>

      <section>
        <h5 class="section-title">Detail Invoice</h5>
        <b-row>
          <b-col md="6">
            <p><strong>No. Kwitansi Pembayaran</strong><br>{{ invoice.invoice_number || '-' }}</p>
            <p><strong>Tanggal</strong><br>{{ formatDate(invoice.date) }}</p>
            <p class="mb-md-0"><strong>Nominal</strong><br>Rp. {{ formatAmount(invoice.amount) }}</p>
          </b-col>
          <b-col md="6">
            <p><strong>Batas Invoice</strong><br>{{ invoice.invoice_limit || '-' }} menit</p>
            <p><strong>Dibuat</strong><br>{{ formatDateTime(invoice.created_at) }}</p>
          </b-col>
        </b-row>
      </section>
      <hr>

      <section>
        <h5 class="section-title">Riwayat DOKU</h5>
        <b-table
          responsive
          striped
          hover
          :items="invoice.logs || []"
          :fields="logFields"
          show-empty
          empty-text="Riwayat tidak tersedia"
        >
          <template v-slot:cell(created_at)="row">
            {{ formatDateTime(row.item.created_at) }}
          </template>
        </b-table>
      </section>
      <hr>

      <div class="text-right mt-3">
        <b-button
          variant="primary"
          :disabled="isSubmitting"
          @click="openExecuteConfirmation"
        >
          Eksekusi Pemulihan
        </b-button>
      </div>
    </b-card>

    <ModalConfirm
      id="modal-confirm-execute-payment-recovery"
      title="Konfirmasi Pemulihan Pembayaran"
      :isSubmitting="isSubmitting"
      desc="Eksekusi akan memperbarui pembayaran. Lanjutkan?"
      @submit="executePayment"
    />

    <b-modal
      id="modal-payment-receipt"
      title="Kwitansi Pemulihan Pembayaran"
      size="xl"
      centered
      ok-only
    >
      <iframe
        v-if="executionFileUrl"
        width="100%"
        height="700"
        :src="executionFileUrl"
        frameborder="0"
        allowfullscreen
      />
    </b-modal>
  </div>
</template>

<script>
const ENDPOINT = 'mnl_exec_122_exc/doku-retry-settlement-after-cancel'
const DOKU_LOCAL_STATUS = ['PENDING', 'PAID', 'CANCELED', 'EXPIRED']
const PAYMENT_LOCAL_STATUS = ['BARU', 'CONFIRMED', 'VOID', 'REQUEST_VOID', 'REJECT']
const DOKU_LOCAL_STATUS_VARIANTS = ['warning', 'success', 'danger', 'secondary']
const PAYMENT_LOCAL_STATUS_VARIANTS = ['info', 'success', 'danger', 'warning', 'danger']

export default {
  name: 'pemulihan-pembayaran',
  data() {
    return {
      input: {
        invoiceNumber: '',
        passcode: '',
        transaksiKeBulanBerikutnya: false,
      },
      isChecking: false,
      isSubmitting: false,
      hasChecked: false,
      checkResult: null,
      executionResult: null,
      logFields: [
        { key: 'created_at', label: 'Waktu' },
        { key: 'action_description', label: 'Aksi' },
        { key: 'notes', label: 'Catatan' },
      ],
    }
  },
  computed: {
    localStatus() {
      return this.checkResult && this.checkResult.local_status
        ? this.checkResult.local_status
        : {}
    },
    dokuResponse() {
      return this.checkResult && this.checkResult.doku_raw_response
        ? this.checkResult.doku_raw_response
        : {}
    },
    invoice() {
      return this.dokuResponse.invoice || {}
    },
    dokuLocalStatus() {
      return DOKU_LOCAL_STATUS[this.localStatus.doku_status_id] || '-'
    },
    paymentLocalStatus() {
      return PAYMENT_LOCAL_STATUS[this.localStatus.gadai_pembayaran_status_id] || '-'
    },
    dokuLocalStatusVariant() {
      return DOKU_LOCAL_STATUS_VARIANTS[this.localStatus.doku_status_id] || 'secondary'
    },
    paymentLocalStatusVariant() {
      return PAYMENT_LOCAL_STATUS_VARIANTS[this.localStatus.gadai_pembayaran_status_id] || 'secondary'
    },
    executionFileUrl() {
      if (!this.executionResult || !this.executionResult.name) return ''
      return this.$baseFilePath + this.executionResult.name
    },
  },
  methods: {
    buildParams(finalize) {
      return {
        invoiceNumber: this.input.invoiceNumber.trim(),
        passcode: this.input.passcode.trim(),
        finalize,
        transaksiKeBulanBerikutnya: this.input.transaksiKeBulanBerikutnya ? 1 : 0,
      }
    },
    formatAmount(value) {
      return value == null ? '-' : this.$helper.addThousandSeparator(value)
    },
    formatDate(value) {
      return value ? this.$helper.changeDateFormat(value) : '-'
    },
    formatDateTime(value) {
      return value
        ? `${this.$helper.changeDateFormat(value)} ${this.$helper.changeDateFormatGetHour(value)}`
        : '-'
    },
    checkPayment() {
      this.isChecking = true
      this.hasChecked = false

      this.$axios.post(ENDPOINT, null, {
        params: this.buildParams(0),
      })
        .then(response => {
          this.checkResult = response.data
          this.hasChecked = true
        })
        .catch(error => {
          this.$helper.parseError(this, error)
        })
        .finally(() => {
          this.isChecking = false
        })
    },
    openExecuteConfirmation() {
      this.$bvModal.show('modal-confirm-execute-payment-recovery')
    },
    executePayment() {
      this.isSubmitting = true

      this.$axios.post(ENDPOINT, null, {
        params: this.buildParams(1),
      })
        .then(response => {
          this.executionResult = response.data
          this.$root.$once('bv::modal::hidden', (event, modalId) => {
            if (modalId === 'modal-confirm-execute-payment-recovery') {
              this.$bvModal.show('modal-payment-receipt')
            }
          })
          this.$bvModal.hide('modal-confirm-execute-payment-recovery')
          return this.refreshCheckResult()
        })
        .catch(error => {
          this.$helper.parseError(this, error)
        })
        .finally(() => {
          this.isSubmitting = false
        })
    },
    refreshCheckResult() {
      return this.$axios.post(ENDPOINT, null, {
        params: this.buildParams(0),
      })
        .then(response => {
          this.checkResult = response.data
          this.hasChecked = true
        })
    },
    resetResults() {
      this.hasChecked = false
      this.checkResult = null
      this.executionResult = null
    },
  },
  watch: {
    'input.invoiceNumber': 'resetResults',
    'input.passcode': 'resetResults',
    'input.transaksiKeBulanBerikutnya': 'resetResults',
  },
}
</script>

<style scoped>
.section-content {
  padding-top: 0.5rem;
}

.section-title {
  font-size: 1.15rem;
  font-weight: 700;
}
</style>
